<template>
  <div class="row">
    <!-- Left container -->
    <div :class="[isRequiredMediaType ? 'col-8' : 'col-12', 'byk__left']">
      <!-- Breadcrumb -->
      <nav aria-label="Breadcrumb" class="byk__left-breadcrumb">
        <ol>
          <li><a href="/">Home</a></li>
          <li aria-current="page">{{ data.title }}</li>
        </ol>
      </nav>

      <!-- Heading -->
      <h3 class="byk__left-title">{{ data.title }}</h3>

      <!-- Tabs component for devices above tab large-->
      <Tabs v-if="isRequiredMediaType" :tabs="filteredTabItems" :onTabChange="handleTabChange" :scrollToTopHeight="36">
        <template v-for="tab in filteredTabItems" :key="tab.key" v-slot:[tab.key]>
          <ProductList
            :heading="activeTabParams.topSectionHeading"
            :products="categorizedProducts?.[tab.key].top"
            expandText="View more"
            collapseText="View less"
            :layer="tab.key"
            subcategory="top"
            :onViewProductDetailsClick="onViewProductDetailsClick"
          />
          <ProductList
            :heading="activeTabParams.bottomSectionHeading"
            :products="categorizedProducts?.[tab.key].bottom"
            :layer="tab.key"
            subcategory="bottom"
            :onViewProductDetailsClick="onViewProductDetailsClick"
          />
        </template>
      </Tabs>

      <!-- Tabs component for devices below tab large-->
      <Tabs v-else :tabs="filteredTabItems" :onTabChange="handleTabChange" :scrollToTopHeight="28">
        <template v-for="tab in filteredTabItems" :key="tab.key" v-slot:[tab.key]>
          <ImagePreview
            :activeTab="activeTabKey"
            :categorizedProducts="categorizedProducts"
            :topImagePlaceholder="activeTabParams.topImagePlaceholder"
            :bottomImagePlaceholder="activeTabParams.bottomImagePlaceholder"
          />
          <Accordion :items="filteredAccordionItems[tab.key]" :isAccordionOpenByDefault="true">
            <template v-slot:top>
              <ProductList
                :products="categorizedProducts?.[tab.key].top"
                expandText="View more"
                collapseText="View less"
                :layer="tab.key"
                subcategory="top"
                :enableSwiper="true"
                :showProductCardInfo="false"
                :onViewProductDetailsClick="onViewProductDetailsClick"
              />
            </template>
            <template v-slot:bottom>
              <ProductList
                :products="categorizedProducts?.[tab.key].bottom"
                :layer="tab.key"
                subcategory="bottom"
                :enableSwiper="true"
                :showProductCardInfo="false"
                :onViewProductDetailsClick="onViewProductDetailsClick"
              />
            </template>
          </Accordion>
        </template>
      </Tabs>
    </div>

    <!-- Here View Selections will render for screens less than tablet -->
    <ViewSelectionsCTA v-if="!isRequiredMediaType" :onViewSelectionsClick="onViewSelectionsClick" />

    <!-- Right container -->
    <div v-if="isRequiredMediaType" class="col-4 byk__right">
      <ImagePreview
        :activeTab="activeTabKey"
        :onViewSelectionsClick="onViewSelectionsClick"
        :categorizedProducts="categorizedProducts"
        :topImagePlaceholder="activeTabParams.topImagePlaceholder"
        :bottomImagePlaceholder="activeTabParams.bottomImagePlaceholder"
      />
    </div>
  </div>
</template>

<script setup>
  import { computed, toRefs, ref, reactive } from 'vue';
  import Tabs from './components/tabs.vue';
  import ProductList from './components/product-list.vue';
  import ImagePreview from './components/image-preview.vue';
  import ViewSelectionsCTA from './components/view-selections-cta.vue';
  import Accordion from '../global/accordion.vue';
  import { useMediaType } from '../../utils/use-media-type';
  import { MEDIA_TYPES } from '../../utils/constants';
  import { useStore } from 'vuex';

  const TAGS = {
    BYK_MIDLAYERS: 'BYK Midlayers',
    BYK_OUTERWEAR: 'BYK Outerwear',
    BYK_BASELAYERS: 'BYK Baselayers',
    MIXMATCH_TOP: 'mixmatch_top',
    MIXMATCH_BOTTOM: 'mixmatch_bottom',
  };

  const props = defineProps({
    data: {
      type: Object,
      required: true,
    },
  });

  const { data } = toRefs(props);
  const mediaType = useMediaType();
  const store = useStore();
  const selectedProductsFromContext = reactive(store?.state?.byk?.selectedProducts);

  // Dynamically generating tab items and accordion items on the basis of bykTabsData which we have set on shopify
  const tabItems = computed(() =>
    data.value?.bykTabsData.map((tab) => ({
      key: tab.identifier.toLowerCase().replace(/ /g, '_'),
      name: tab.tabName,
    }))
  );

  const accordionItems = computed(() => {
    const items = {};
    data.value.bykTabsData.forEach((tab) => {
      const layerKey = tab.identifier.toLowerCase().replace(/ /g, '_');
      items[layerKey] = [
        { title: tab.topSectionHeading, key: 'top' },
        { title: tab.bottomSectionHeading, key: 'bottom' },
      ];
    });
    return items;
  });

  const categorizedProducts = computed(() => {
    // Initialize the structure with empty arrays
    const structure = tabItems.value.reduce((acc, tab) => {
      acc[tab.key] = { top: [], bottom: [] };
      return acc;
    }, {});

    // Map to associate layer tags with their keys
    const layerTagMap = {
      [TAGS.BYK_MIDLAYERS]: 'mid_layer',
      [TAGS.BYK_OUTERWEAR]: 'outer_layer',
      [TAGS.BYK_BASELAYERS]: 'base_layer',
    };

    // Iterate over the products and categorize them
    data.value.products.forEach((product) => {
      if (!product.available) return;

      const tags = product.tags || [];
      if (!tags.length) return; // Early exit if no tags

      const layerKey = Object.keys(layerTagMap).find((tag) => tags.some((t) => t.includes(tag)));

      if (layerKey) {
        const key = layerTagMap[layerKey];

        if (tags.includes(TAGS.MIXMATCH_TOP)) {
          structure[key]?.top?.push(product);
        }

        if (tags.includes(TAGS.MIXMATCH_BOTTOM)) {
          structure[key]?.bottom?.push(product);
        }
      }
    });

    return structure;
  });

  // Avoiding empty tabs to render
  const filteredTabItems = computed(() => {
    return tabItems.value?.filter(({ key }) => {
      const { top = [], bottom = [] } = categorizedProducts.value?.[key] || {};
      return top.length > 0 || bottom.length > 0;
    });
  });

  // Initialize activeTab with the default tab key
  const activeTabKey = ref(filteredTabItems.value?.[0]?.key);
  const activeTabParams = ref(
    data.value?.bykTabsData?.find((item) => item.identifier?.toLowerCase()?.replace(/ /g, '_') === activeTabKey.value)
  );

  // Avoiding empty accordion to render
  const filteredAccordionItems = computed(() => {
    // Filter out empty items
    return Object.keys(accordionItems.value).reduce((filteredItems, layer) => {
      const items = accordionItems.value[layer].filter((item) => {
        const products = categorizedProducts.value?.[layer]?.[item.key] || [];
        return products.length > 0;
      });

      if (items.length > 0) {
        filteredItems[layer] = items;
      }

      return filteredItems;
    }, {});
  });

  const isRequiredMediaType = computed(() =>
    [MEDIA_TYPES.TABLET_EXTRA_LARGE, MEDIA_TYPES.DESKTOP, MEDIA_TYPES.DESKTOP_LARGE].includes(mediaType.value)
  );

  const handleTabChange = (tabKey) => {
    activeTabKey.value = tabKey;
    activeTabParams.value = data.value?.bykTabsData?.find(
      (item) => item.identifier?.toLowerCase()?.replace(/ /g, '_') === activeTabKey.value
    );
  };

  const onViewSelectionsClick = () => {
    window.toggleDrawer(true, 'ViewSelectionsBYK', {
      bykSelectedProducts: selectedProductsFromContext,
      heading: 'Your Selections',
      onRemoveItem,
      onUpdatingOptions,
      resetBYKSelections,
    });
  };

  const onViewProductDetailsClick = (data) => {
    const { product, selectedColor, layer, subcategory } = data;
    const selectedProduct = selectedProductsFromContext[layer][subcategory]?.[0];
    const selectedVariant = selectedProduct?.selectedVariantDetails;

    window.toggleDrawer(true, 'QuickView', {
      product,
      selectedColor,
      layer,
      subcategory,
      onChangeOptionFromProductDetails,
      hideUnavailableOption: true,
      hideProductDesc: true,
      hideColorOption: true,
      hideATC: true,
      hideFreeShippingLabel: true,
      hideStockInfo: true,
      enableSizeGuide: true,
      enableProductDetails: true,
      showWishlistIcon: false,
      selectedSize: selectedProduct?.id === product?.id ? selectedVariant?.size : '',
      selectedSeam: selectedProduct?.id === product?.id ? selectedVariant?.inseam || selectedVariant?.outseam : '',
      seamType: selectedVariant?.outseam ? 'Outseam' || '' : selectedVariant?.inseam ? 'Inseam' : '', // It could be possible product doesn't have any seam
    });
  };

  const onRemoveItem = (product, layer, subcategory) => {
    store.dispatch('updateBYKSelectedProduct', {
      layer: layer,
      subcategory: subcategory,
      value: [], // currently only single product is supposed to be there, so we can empty the whole array on remove
    });
  };

  const onUpdatingOptions = (layer, subcategory, updatedProduct = {}) => {
    store.dispatch('updateBYKSelectedProduct', {
      layer: layer,
      subcategory: subcategory,
      value: [updatedProduct], // currently only single product is supposed to be there
    });
  };

  const resetBYKSelections = () => {
    store.dispatch('resetBYKSelectedProducts');
  };

  const onChangeOptionFromProductDetails = (product, layer, subcategory, selectedOptionsObj) => {
    const selectedProduct = selectedProductsFromContext[layer][subcategory]?.[0];

    const updateVariantDetails = (baseProduct, isSameProduct) => ({
      ...baseProduct?.selectedVariantDetails,
      size: selectedOptionsObj?.Size || baseProduct?.selectedVariantDetails?.size || '',
      ...(selectedOptionsObj?.Inseam || baseProduct?.selectedVariantDetails?.inseam
        ? { inseam: selectedOptionsObj?.Inseam || baseProduct?.selectedVariantDetails?.inseam }
        : {}),
      ...(selectedOptionsObj?.Outseam || baseProduct?.selectedVariantDetails?.outseam
        ? { outseam: selectedOptionsObj?.Outseam || baseProduct?.selectedVariantDetails?.outseam }
        : {}),
      variantId: selectedOptionsObj?.selectedVariant?.id || baseProduct?.selectedVariantDetails?.variantId || '',
      ...(isSameProduct
        ? {}
        : {
            color: selectedOptionsObj?.Color || '',
            selectedFeatureImage: getFeatureImage(
              baseProduct,
              selectedOptionsObj?.selectedVariant?.featured_image,
              selectedOptionsObj?.Color || ''
            ),
          }),
    });

    const updatedProduct =
      product?.id === selectedProduct?.id
        ? { ...selectedProduct, selectedVariantDetails: updateVariantDetails(selectedProduct, true) }
        : { ...product, selectedVariantDetails: updateVariantDetails(product, false) };

    if (Object.keys(updatedProduct).length) {
      store.dispatch('updateBYKSelectedProduct', {
        layer,
        subcategory,
        value: [updatedProduct], // currently only single product is supposed to be there
      });
    }
  };

  const getFeatureImage = (product, imgURL, color) => {
    if (imgURL) {
      return { src: imgURL, alt: color };
    }

    const { images, title } = product;
    const imageData = window.getProductImages(images, color, title);

    return imageData[0];
  };
</script>
